import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import { useI18n } from "../context/I18nContext";
import { motion } from "framer-motion";
import { Video, Heart, Globe2, ArrowRight, Sparkles, Check, Mic, Languages } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1758598304200-f89a1d8ebedb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwzfHxwZW9wbGUlMjB2aWRlbyUyMGNhbGwlMjBzbWlsaW5nfGVufDB8fHx8MTc4MzMwMTM5Nnww&ixlib=rb-4.1.0&q=85";
const COMMUNITY = "https://images.unsplash.com/photo-1607748862156-7c548e7e98f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwZnJpZW5kcyUyMGxhdWdoaW5nfGVufDB8fHx8MTc4MzMwMTM5Nnww&ixlib=rb-4.1.0&q=85";
const HERO_PEER = "https://images.unsplash.com/photo-1586985564150-11ee04838034?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxwZW9wbGUlMjB2aWRlbyUyMGNhbGwlMjBzbWlsaW5nfGVufDB8fHx8MTc4MzMwMTM5Nnww&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { t } = useI18n();
  const steps = [
    { icon: Languages, kicker: t("landing.step1Kicker"), title: t("landing.step1Title"), desc: t("landing.step1Desc") },
    { icon: Video, kicker: t("landing.step2Kicker"), title: t("landing.step2Title"), desc: t("landing.step2Desc") },
    { icon: Sparkles, kicker: t("landing.step3Kicker"), title: t("landing.step3Title"), desc: t("landing.step3Desc") },
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-12 sm:pt-20 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-primary bg-primary/10 rounded-full px-4 py-1.5">
              <Globe2 className="w-4 h-4" /> {t("landing.badge")}
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-heading font-black tracking-tighter leading-[1.05]">
              {t("landing.h1a")}<br />
              <span className="text-primary">{t("landing.h1b")}</span>
            </h1>
            <p className="mt-6 text-lg text-foreground/70 max-w-xl leading-relaxed">{t("landing.sub")}</p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/register">
                <Button size="lg" className="rounded-full text-base px-7 h-12" data-testid="hero-get-started">
                  {t("landing.cta1")} <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="rounded-full text-base px-7 h-12" data-testid="hero-login">
                  {t("landing.cta2")}
                </Button>
              </Link>
            </div>
            <p className="mt-6 text-sm text-muted-foreground flex items-center gap-2">
              <Heart className="w-4 h-4 text-primary" /> {t("landing.tagline")}
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:col-span-5">
            <div className="relative">
              <div className="soft-img">
                <img src={HERO} alt="Video conversation" className="w-full h-[420px] object-cover" />
              </div>
              <div className="absolute -bottom-5 -left-5 backdrop-blur-lg bg-card/80 border border-white/10 shadow-lg rounded-2xl p-4 hidden sm:block">
                <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">{t("landing.nowPracticing")}</p>
                <p className="font-heading font-bold text-lg">Español 🇪🇸 ↔ English 🇬🇧</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-primary bg-primary/10 rounded-full px-4 py-1.5">
            <Video className="w-4 h-4" /> {t("landing.liveBadge")}
          </span>
          <h2 className="mt-5 text-2xl sm:text-3xl lg:text-4xl font-heading font-bold tracking-tight">{t("landing.howTitle")}</h2>
          <p className="text-foreground/70 mt-2 max-w-xl mx-auto">{t("landing.howSub")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          {steps.map((s, i) => (
            <motion.div key={s.kicker} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.12 }}
              className="rounded-3xl bg-card border border-border shadow-sm p-6 flex flex-col">
              <div className="rounded-2xl bg-[#141010] border border-white/10 h-44 p-4 overflow-hidden relative">
                {i === 0 && (
                  <div className="h-full flex flex-col justify-center gap-3">
                    <div className="flex items-center gap-3">
                      <span className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center font-heading font-bold text-primary">JD</span>
                      <div className="h-2.5 w-24 rounded-full bg-white/20" />
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest font-bold text-white/40 mb-1.5">{t("landing.iSpeak")}</p>
                      <span className="text-xs font-semibold bg-secondary/20 text-secondary rounded-full px-3 py-1">English</span>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest font-bold text-white/40 mb-1.5">{t("landing.imLearning")}</p>
                      <span className="text-xs font-semibold bg-primary/20 text-primary rounded-full px-3 py-1">Español</span>
                    </div>
                  </div>
                )}
                {i === 1 && (
                  <div className="h-full grid grid-cols-2 gap-2">
                    <img src={HERO} alt="" className="w-full h-full object-cover rounded-xl" />
                    <img src={HERO_PEER} alt="" className="w-full h-full object-cover rounded-xl" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="w-11 h-11 rounded-full bg-primary flex items-center justify-center text-primary-foreground shadow-lg animate-pulse">
                        <Video className="w-5 h-5" />
                      </span>
                    </div>
                    <span className="absolute bottom-2 left-2 flex items-center gap-1 text-[10px] font-bold text-white bg-black/50 rounded-full px-2 py-0.5">
                      <Mic className="w-3 h-3" /> LIVE
                    </span>
                  </div>
                )}
                {i === 2 && (
                  <div className="h-full relative">
                    <img src={HERO_PEER} alt="" className="absolute inset-0 w-full h-full object-cover rounded-xl" />
                    <div className="absolute inset-0 bg-black/20 rounded-xl" />
                    <span className="absolute bottom-2 right-2 w-14 h-10 rounded-lg overflow-hidden border border-white/40">
                      <img src={HERO} alt="" className="w-full h-full object-cover" />
                    </span>
                    <div className="absolute top-2 left-2 w-32 backdrop-blur-md bg-white/90 rounded-xl p-2.5 shadow-lg">
                      <p className="text-[8px] uppercase tracking-widest font-bold text-black/40">Español · Beginner</p>
                      <p className="text-sm font-heading font-black text-[#25201b] leading-tight">Encantado</p>
                      <p className="text-[10px] text-black/60">= Nice to meet you</p>
                      <div className="mt-1.5 flex items-center justify-center gap-1 text-[9px] font-bold text-secondary-foreground bg-secondary rounded-full py-1">
                        <Check className="w-3 h-3" /> Correct · +10
                      </div>
                    </div>
                    <span className="absolute top-2 right-2 flex items-center gap-1 text-[10px] font-bold text-secondary bg-black/50 rounded-full px-2 py-0.5">
                      <Sparkles className="w-3 h-3" /> 40 pts
                    </span>
                  </div>
                )}
              </div>

              <div className="mt-5">
                <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-primary">{s.kicker}</span>
                <h3 className="font-heading font-bold text-xl mt-1">{s.title}</h3>
                <p className="text-foreground/70 mt-2 leading-relaxed text-sm">{s.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="rounded-3xl overflow-hidden relative bg-secondary/20 border border-border grid grid-cols-1 md:grid-cols-2">
          <div className="p-10 sm:p-14 flex flex-col justify-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold tracking-tight">
              {t("landing.communityH2a")}<br /> {t("landing.communityH2b")}
            </h2>
            <p className="text-foreground/70 mt-4 leading-relaxed max-w-md">{t("landing.communitySub")}</p>
            <Link to="/register" className="mt-8">
              <Button size="lg" className="rounded-full px-7 h-12" data-testid="community-cta">{t("landing.communityCta")}</Button>
            </Link>
            <Link to="/vision" data-testid="community-vision-link" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-secondary hover:gap-2.5 transition-all">
              <Globe2 className="w-4 h-4" /> Read our vision <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <img src={COMMUNITY} alt="Friends laughing" className="w-full h-full object-cover min-h-[280px]" />
        </div>
      </section>
    </div>
  );
}
